import {
    Search as SearchIcon,
    OndemandVideo as WatchedIcon,
    SvgIconComponent,
    AccessTime as ThisWeekIcon,
    Update as NextWeekIcon,
    Restore as LastWeekIcon,
    GroupAdd as AddGroupIcon,
    MonetizationOn as BiddersIcon,
    DriveEta as PipleLineVehicleIcon,
    ContactPhone as ContactLogIcon,
    AddAPhoto as AddPhotoIcon,
    AssignmentTurnedIn as CheckedInIcon,
    Receipt as ReceiptIcon,
    Web as ViewedOnlineIcon,
    Star as StarIcon,
    Description as Documents,
} from '@mui/icons-material'

import BiddersThisWeek from './BiddersThisWeek'
import PipelineVehicleListings from './PipelineVehicleListings'
import ConsignedVehicles from './ConsignedVehicles'
import MyContactLogFollowups from './MyContactLogFollowups'
import NeedPhotos from './NeedPhotos'
import NewCustomersThisWeek from './NewCustomersThisWeek'
import Favorites from './Favorites'
import SearchCard from './SearchCard/SearchCard'
import ContactLogSearchCard from './SearchCard/ContactLogSearchCard'
import VDPViews from './VDPViews'
import VehiclesCheckedInCount from './VehiclesCheckedInCount'
import WatchedVehicles from './WatchedVehicles'
import SalesThisWeek from './SalesOverview/SalesThisWeek'
import SalesLastWeek from './SalesOverview/SalesLastWeek'
import RegisteredNextWeek from './RegisteredNextWeek'
import VehiclesWithDocuments from './VehiclesWithDocuments'
import CompletedVehicles from './CompletedVehicles'
import type { BasicWidgetProps } from '../types'
import type { WidgetName } from './WidgetNames'
import { FeatureFlagRoles } from '@edge/spark/io-roles'
import {
    SearchCardConfigurationModal,
    type WidgetConfigurationModalProps,
} from '../CardConfigurator/ConfigurationModal/SearchCardConfigurationModal'
import UnifiedPSIEligible from './UnifiedPSIEligible/UnifiedPSIEligible'
import OdsOffers from './OdsIfOffers/OdsOffers'

export enum WidgetCategories {
    SEARCH = 'Search',
    COMPANY_SEARCH = 'CompanySearch',
    SALES = 'Sales',
    VEHICLES = 'Vechicles',
    TEMPORARY_FLAG_TO_AUCTION_EDGE_ADMIN_ONLY = 'AuctionEdgeAdminOnly',
}

export interface AddableWidget {
    name: WidgetName
    component: React.FC<BasicWidgetProps>
    /** User must have one of the roles in the array to see this widget */
    requiredRoleOption?: string[]
    featureFlags?: {
        include?: string[]
        exclude?: string[]
    }
    categories: WidgetCategories[]
    /** Displays on the side panel when in "Add Card" mode. */
    widgetDescription: string
    Icon: SvgIconComponent
    /** Use SearchCardConfigurationModal? */
    Modal: React.FunctionComponent<WidgetConfigurationModalProps>
}

const LD_FEATURE_FLAGS = {
    releasePsiPlus: 'releasePsiPlus',
}

export const addableWidgetList: AddableWidget[] = [
    {
        name: 'UnifiedAPIEligiblePSI',
        component: UnifiedPSIEligible,
        // We are using a Card categories instead of the requiredRoleOption to feature flag that, because the FeatureFlagRoles enum had the Auction Edge Admin one commented out, and as we are not aware of side effects of uncommenting that I would rather go around it.
        categories: [WidgetCategories.TEMPORARY_FLAG_TO_AUCTION_EDGE_ADMIN_ONLY],
        featureFlags: {
            include: [LD_FEATURE_FLAGS.releasePsiPlus],
        },
        widgetDescription: 'Vehicles Eligible for PSI+',
        Icon: PipleLineVehicleIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'SearchCard',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Custom Vehicle Card',
        Icon: PipleLineVehicleIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'ContactLogSearchCard',
        component: ContactLogSearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Custom Contact Log Card',
        Icon: ContactLogIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'SalesThisWeek',
        component: SalesThisWeek,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Overview This Week',
        Icon: ThisWeekIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'SalesLastWeek',
        component: SalesLastWeek,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Overview Last Week',
        Icon: LastWeekIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'RegisteredNextWeek',
        component: RegisteredNextWeek,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Registered Next Week',
        Icon: NextWeekIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'NeedPhotos',
        component: NeedPhotos,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles Need Photos',
        Icon: AddPhotoIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'NeedPhotosPreSale',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles Need Photos (Pre-Sale)',
        Icon: AddPhotoIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'ConsignedVehicles',
        component: ConsignedVehicles,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles Need CR',
        Icon: ReceiptIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'NeedCRPreSale',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles Need CR (Pre-Sale)',
        Icon: ReceiptIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'VehiclesCheckedInCount',
        component: VehiclesCheckedInCount,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles Checked In',
        Icon: CheckedInIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'BiddersThisWeek',
        component: BiddersThisWeek,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Bidders',
        Icon: BiddersIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'NewCustomersThisWeek',
        component: NewCustomersThisWeek,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'New Customers',
        Icon: AddGroupIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'MyContactLogFollowups',
        component: MyContactLogFollowups,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Contact Followups',
        Icon: ContactLogIcon,
        Modal: SearchCardConfigurationModal,
    },
    // {
    //   name: 'BirthdaysThisWeek',
    //   component: BirthdaysThisWeek,
    //   requiredRoleOption: ['FEATURE_FLAG_2'],
    //   categories: [WidgetCategories.SEARCH],
    //   widgetDescription: 'Birthdays This Month Card',
    //   Icon: BirthdayIcon,
    // },
    {
        name: 'PipelineVehicleListings',
        component: PipelineVehicleListings,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Pipeline Vehicle Listings',
        Icon: PipleLineVehicleIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'VDPViews',
        component: VDPViews,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'VDP Views',
        Icon: ViewedOnlineIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'WatchedVehicles',
        component: WatchedVehicles,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Watched Vehicles',
        Icon: WatchedIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'Favorites',
        component: Favorites,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'My Favorites',
        Icon: StarIcon,
        Modal: SearchCardConfigurationModal,
    },
    // { disable until until detail view is created
    //   name: 'FavoriteBuyers',
    //   component: FavoriteBuyers,
    //   categories: [WidgetCategories.SEARCH],
    //   widgetDescription: 'Favorite Buyers',
    //   Icon: StarIcon,
    // },
    // { disable until until detail view is created
    //   name: 'FavoriteSellers',
    //   component: FavoriteSellers,
    //   categories: [WidgetCategories.SEARCH],
    //   widgetDescription: 'Favorite Sellers',
    //   Icon: StarIcon,
    // },
    {
        name: 'PendingOffers',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Pending Offers',
        Icon: SearchIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'PendingArbitrations',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Pending Arbitrations',
        Icon: SearchIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'NeedsTitles',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Needs Titles',
        Icon: SearchIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'DMSVehicles',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'DMS Vehicles',
        Icon: SearchIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'VehiclesWithDocuments',
        component: VehiclesWithDocuments,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles with Documents',
        Icon: Documents,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'EligiblePSI',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Vehicles Eligible for PSI',
        featureFlags: {
            exclude: [LD_FEATURE_FLAGS.releasePsiPlus],
        },
        Icon: ReceiptIcon,
        requiredRoleOption: [FeatureFlagRoles.PSI_MODULE],
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'PSIOrderedTotal',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'PSI Orders',
        featureFlags: {
            exclude: [LD_FEATURE_FLAGS.releasePsiPlus],
        },
        Icon: ReceiptIcon,
        requiredRoleOption: [FeatureFlagRoles.PSI_MODULE],
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'PSIOrderedSpark',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Spark PSI Orders',
        featureFlags: {
            exclude: [LD_FEATURE_FLAGS.releasePsiPlus],
        },
        Icon: ReceiptIcon,
        requiredRoleOption: [FeatureFlagRoles.PSI_MODULE],
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'PSIOrderedAMS',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'AMS PSI Orders',
        featureFlags: {
            exclude: [LD_FEATURE_FLAGS.releasePsiPlus],
        },
        Icon: ReceiptIcon,
        requiredRoleOption: [FeatureFlagRoles.PSI_MODULE],
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'PSIOrderedPipeLine',
        component: SearchCard,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Pipeline PSI Orders',
        featureFlags: {
            exclude: [LD_FEATURE_FLAGS.releasePsiPlus],
        },
        Icon: ReceiptIcon,
        requiredRoleOption: [FeatureFlagRoles.PSI_MODULE],
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'CompletedVehicles',
        component: CompletedVehicles,
        categories: [WidgetCategories.SEARCH],
        widgetDescription: 'Sold / Exited / Redeemed / Returned',
        Icon: PipleLineVehicleIcon,
        Modal: SearchCardConfigurationModal,
    },
    {
        name: 'BetaIfs',
        component: OdsOffers,
        categories: [WidgetCategories.SEARCH],
        featureFlags: {
            include: [LD_FEATURE_FLAGS.sierraOfferCard],
        },
        widgetDescription: 'Beta Ifs',
        Icon: SearchIcon,
        Modal: SearchCardConfigurationModal,
    },
]

export const widgets = addableWidgetList
export default widgets
